import {
    AutocompleteHit,
    Box,
    ClickAwayListener,
    MenuItem,
    MenuList,
    SearchField,
    Searchtree,
    SearchtreeTip,
    Stack,
    StyledSearchFieldBox,
    Typography,
} from "@tm/components"
import { useUser, useWorkTaskId } from "@tm/context-distribution"
import { Autocomplete } from "@tm/data"
import { useLocalization } from "@tm/localization"
import { channel, RequestArticleListPayload, TreeNode } from "@tm/models"
import { concat, encodeUniqueId, renderRoute, TmaHelper, uniqueId } from "@tm/utils"
import { useCallback, useEffect, useState } from "react"
import { useHistory, useParams } from "react-router"
import useBreadCrumbHandler from "../../../hooks/breadCrumbhandler/useBreadCrumbHandler"
import { getBundleParams } from "../../../utils"
import { SearchTree } from "../../widget/business"
import Hint from "../DirectSearchField/components/Hint"

type Props = {
    hideBestseller?: boolean
    showTreeTip?: boolean
    tree?: SearchTree
    universalSearchRoute: string
    onApplyProductGroups?(productGroups: number[]): void
}

export default function UniSearchWidget({ universalSearchRoute, tree, onApplyProductGroups, hideBestseller, showTreeTip }: Props) {
    const { searchtreeNodeVariant, minimumSearchLength } = getBundleParams()
    const mode = searchtreeNodeVariant === "fastClick" ? "fastClick" : "fast-click-2.0"

    const { userContext } = useUser()
    const { translateText } = useLocalization()
    const params = useParams()
    const workTaskId = useWorkTaskId()
    const history = useHistory()
    const { newBreadcrumbSelected, breadCrumbState, isLoading, resetSelectedNode } = useBreadCrumbHandler(undefined, "universalParts")
    const [currentShortcutKey, setCurrentShortcutKey] = useState<string>("")
    const [query, setQuery] = useState<string>("")
    const [hint, setHint] = useState<string | undefined>(undefined)
    const [suggestions, setSuggestions] = useState<Autocomplete.ProductGroupHit[]>([])
    const [searchFieldFocused, setSearchFieldFocused] = useState(false)
    const [selectedSuggest, setSelectedSuggest] = useState<string>()
    const [suggestionsOpen, setSuggestionsOpen] = useState(false)

    useEffect(() => {
        const unsubscribe = channel("APP").subscribe("SHORTCUTS", (rez) => {
            if (rez.key === "UNI") {
                setCurrentShortcutKey(rez.key + Math.random())
            }
        })
        return unsubscribe
    }, [])

    const handleUniSearch = useCallback(
        (searchQuery: string) => {
            if (searchQuery.length < minimumSearchLength.universalSearch) {
                setHint(translateText(812).replace("{0}", minimumSearchLength.universalSearch.toString()))
                return
            }

            const url = renderRoute(universalSearchRoute, {
                ...params,
                workTaskId: encodeUniqueId(workTaskId ?? uniqueId()),
            })
            TmaHelper.UniParts.Search.KeywordSearchFromStartPage(searchQuery, `${url}?query=${searchQuery}`)
            history.push(`${url}?query=${searchQuery}`)
        },
        [history, minimumSearchLength.universalSearch, params, universalSearchRoute, translateText, workTaskId]
    )

    const handleNodeClick = useCallback(
        (node: TreeNode, fastClick?: boolean) => {
            newBreadcrumbSelected(node)
            if ((!node.hasChildNodes && breadCrumbState.treeId) || (fastClick && breadCrumbState.treeId)) {
                const request: RequestArticleListPayload = {
                    uniNode: { treeId: breadCrumbState.treeId, nodeId: node.id, history: [] },
                }

                TmaHelper.UniParts.SearchTree.NodeSelected({ mode, nodeId: node.id, treeId: breadCrumbState.treeId })
                resetSelectedNode()
                channel("WORKTASK").publish("PARTS/REQUEST_LIST", request)
            }
        },
        [breadCrumbState.treeId, mode, newBreadcrumbSelected, resetSelectedNode]
    )

    const handleOnBreadCrumbClick = useCallback(
        (node: TreeNode) => {
            newBreadcrumbSelected(node)
        },
        [newBreadcrumbSelected]
    )

    function renderTree() {
        if (!tree) {
            return null
        }

        return (
            <Searchtree
                tree={breadCrumbState.initialTree}
                breadCrumbs={breadCrumbState.breadCrumbs}
                selectedNode={breadCrumbState?.selectedNode}
                applyProductGroups={onApplyProductGroups}
                showBestseller={!hideBestseller}
                onNodeClick={handleNodeClick}
                onBreadCrumbClick={handleOnBreadCrumbClick}
                loading={isLoading}
                variant="full"
                layout="small"
                hideSecondLevel
            />
        )
    }

    const handleKeyStroke = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const selectionIndex = suggestions.findIndex((x) => x.productGroup === selectedSuggest)
            if (e.code === "ArrowUp") {
                e.preventDefault()
                if (selectionIndex <= 0) {
                    setSelectedSuggest(suggestions[suggestions.length - 1]?.productGroup)
                } else {
                    setSelectedSuggest(suggestions[selectionIndex - 1]?.productGroup)
                }
            } else if (e.code === "ArrowDown") {
                e.preventDefault()
                if (selectionIndex === -1 || selectionIndex === suggestions.length - 1) {
                    setSelectedSuggest(suggestions[0]?.productGroup)
                } else {
                    setSelectedSuggest(suggestions[selectionIndex + 1]?.productGroup)
                }
            } else if (e.code === "Enter" && selectedSuggest) {
                e.preventDefault()
                handleUniSearch(selectedSuggest)
            }
        },
        [handleUniSearch, selectedSuggest, suggestions]
    )

    const handleOnChange = useCallback(async (newQuery: string) => {
        setQuery(newQuery)
        setHint(undefined)

        if (newQuery.length >= 2) {
            setSuggestions(await Autocomplete.getUniversalProductGroups(userContext, newQuery))
        } else {
            setSuggestions([])
        }
    }, [])

    useEffect(() => {
        setSuggestionsOpen(searchFieldFocused && suggestions.length > 0)
    }, [searchFieldFocused, suggestions])

    return (
        <>
            <ClickAwayListener onClickAway={() => setSearchFieldFocused(false)}>
                <Box className="upper">
                    {hint && hint.length > 0 && <Hint hint={hint} />}
                    <SearchField
                        key={currentShortcutKey}
                        placeholder={translateText(194)}
                        onStartSearch={() => handleUniSearch(query)}
                        value={query}
                        size="large"
                        sx={{
                            width: "100%",
                            ".MuiFilledInput-root": {
                                padding: 0,
                                flexWrap: "nowrap",
                            },
                            "& .MuiOutlinedInput-root": {
                                borderTopLeftRadius: 4,
                                borderTopRightRadius: 4,
                                borderBottomLeftRadius: suggestionsOpen ? 0 : 4,
                                borderBottomRightRadius: suggestionsOpen ? 0 : 4,
                            },
                        }}
                        onChange={handleOnChange}
                        onFocus={() => setSearchFieldFocused(true)}
                        onKeyDown={handleKeyStroke}
                    >
                        {suggestionsOpen && !!suggestions.length && (
                            <StyledSearchFieldBox>
                                <MenuList variant="menu">
                                    {suggestions.map((suggestion) => (
                                        <MenuItem
                                            selected={selectedSuggest === suggestion.productGroup}
                                            key={`${suggestion.productGroupSynonym ?? suggestion.productGroup}-${suggestion.dataSupplier}`}
                                            onClick={() => handleUniSearch(concat(" ", suggestion.productGroup, suggestion.dataSupplier))}
                                        >
                                            <Stack>
                                                <span>
                                                    <AutocompleteHit>
                                                        {concat(
                                                            " | ",
                                                            suggestion.highlightProductGroupSynonym ??
                                                                suggestion.highlightProductGroup ??
                                                                suggestion.productGroupSynonym ??
                                                                suggestion.productGroup,
                                                            suggestion.highlightDataSupplier ?? suggestion.dataSupplier
                                                        )}
                                                    </AutocompleteHit>
                                                </span>
                                                {!!suggestion.productGroupSynonym && !!suggestion.productGroup && (
                                                    <Typography variant="body2">{suggestion.productGroup}</Typography>
                                                )}
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </StyledSearchFieldBox>
                        )}
                    </SearchField>

                    {mode === "fast-click-2.0" && showTreeTip && <SearchtreeTip />}
                </Box>
            </ClickAwayListener>

            {renderTree()}
        </>
    )
}
